/* Khatmah plans — several at once, each with its own scope, rhythm and
   bookmark: e.g. the whole Qurʾan cover to cover, Al-Baqarah every two weeks,
   Yā-Sīn every day. Pure functions; unit-tested (tests/unit/khatmah.test.ts). */
import { addDays } from '@shared/prayer';
import { CHAPTERS, JUZS } from '@/features/quran/api';
import type { Stamped } from '@/lib/types';

export type KhatmahScope =
  | { kind: 'quran' }
  | { kind: 'surah'; surah: number }
  | { kind: 'juz'; juz: number }
  | { kind: 'range'; from: string; to: string };

export interface KhatmahCycle { every: number; unit: 'day' | 'week' | 'month' }

export interface Khatmah extends Stamped {
  id: string;
  title: string;
  scope: KhatmahScope;
  /** null = no deadline (read at your own pace) */
  cycle: KhatmahCycle | null;
  /** āyāt completed in the current cycle (index of the next āyah) */
  position: number;
  cycleStart: string;
  completions: number;
  /** āyāt read today for this plan */
  todayKey?: string;
  todayCount?: number;
  createdAt: number;
}

/** pages: mushaf pages finished in the focus reader (absent on days logged before it was counted). */
export interface AyahDay extends Stamped { dayKey: string; ayahs: number; letters: number; hasanat: number; pages?: number }

const scopeCache = new Map<string, string[]>();

/** Ordered verse keys covered by a scope. */
export function scopeKeys(scope: KhatmahScope): string[] {
  const cacheKey = JSON.stringify(scope);
  const hit = scopeCache.get(cacheKey);
  if (hit) return hit;
  const keys: string[] = [];
  const surahAyahs = (s: number, from = 1, to = CHAPTERS[s - 1].v) => { for (let a = from; a <= to; a++) keys.push(`${s}:${a}`); };
  if (scope.kind === 'quran') for (let s = 1; s <= 114; s++) surahAyahs(s);
  else if (scope.kind === 'surah') surahAyahs(scope.surah);
  else if (scope.kind === 'juz') {
    const j = JUZS[scope.juz - 1];
    for (const [s, range] of Object.entries(j.map).sort((a, b) => Number(a[0]) - Number(b[0]))) {
      const [a, b] = range.split('-').map(Number);
      surahAyahs(Number(s), a, b);
    }
  } else {
    const [fs, fa] = scope.from.split(':').map(Number);
    const [ts, ta] = scope.to.split(':').map(Number);
    for (let s = fs; s <= ts; s++) surahAyahs(s, s === fs ? fa : 1, s === ts ? ta : CHAPTERS[s - 1].v);
  }
  scopeCache.set(cacheKey, keys);
  return keys;
}

export function scopeLabel(scope: KhatmahScope): string {
  if (scope.kind === 'quran') return "The whole Qurʾan";
  if (scope.kind === 'surah') return `Sūrat ${CHAPTERS[scope.surah - 1].n}`;
  if (scope.kind === 'juz') return `Juz ${scope.juz}`;
  return `${scope.from} – ${scope.to}`;
}

export function cycleDays(c: KhatmahCycle | null): number | null {
  if (!c) return null;
  return c.unit === 'day' ? c.every : c.unit === 'week' ? c.every * 7 : c.every * 30;
}

export function cycleLabel(c: KhatmahCycle | null): string {
  if (!c) return 'At your own pace';
  if (c.every === 1) return c.unit === 'day' ? 'Every day' : c.unit === 'week' ? 'Every week' : 'Every month';
  return `Every ${c.every} ${c.unit}s`;
}

const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 864e5);

/* A daily plan (e.g. Yā-Sīn every day) starts afresh each day; longer cycles
   keep your bookmark and simply show you're behind if the window passes. */
export function rollover(k: Khatmah, today: string): Khatmah {
  let next = k;
  if (k.todayKey !== today) next = { ...next, todayKey: today, todayCount: 0 };
  const len = cycleDays(k.cycle);
  if (len && daysBetween(k.cycleStart, today) >= len) {
    const resets = len === 1 || k.position === 0;
    next = { ...next, cycleStart: today, ...(resets ? { position: 0 } : {}) };
  }
  return next;
}

export interface KhatmahProgress {
  total: number;
  done: number;
  pct: number;
  /** āyāt per day to finish on time (null without a deadline) */
  dailyTarget: number | null;
  todayDone: number;
  daysLeft: number | null;
  /** how far behind the steady pace, in āyāt (0 if on track) */
  behind: number;
  /** of today's target: the āyāt owed from days you fell behind (the rest is today's own share) */
  catchUp: number;
  /** today's target is reached — time to stop (always false for a plan with no deadline, which has no daily target) */
  doneToday: boolean;
  nextKey: string | null;
}

export function khatmahProgress(k: Khatmah, today: string): KhatmahProgress {
  const keys = scopeKeys(k.scope);
  const total = keys.length;
  const done = Math.min(k.position, total);
  const len = cycleDays(k.cycle);
  // Never more than this round's progress: a plan finished earlier today (before the fix that
  // resets the count) kept the old round's āyāt, which showed as owed in the new round.
  const todayDone = Math.min(k.todayKey === today ? k.todayCount ?? 0 : 0, k.position);
  let dailyTarget: number | null = null, daysLeft: number | null = null, behind = 0, catchUp = 0;
  if (len) {
    const elapsed = Math.max(0, daysBetween(k.cycleStart, today));
    daysLeft = Math.max(1, len - elapsed);
    // Where a steady pace would have you at the start of today (today's portion isn't due until the day is over).
    const expected = Math.min(total, Math.round((total * elapsed) / len));
    behind = Math.max(0, expected - done);
    ({ target: dailyTarget, catchUp } = dailyPace(total, done - todayDone, expected, daysLeft, len));
  }
  return { total, done, pct: total ? done / total : 0, dailyTarget, todayDone, daysLeft, behind, catchUp, doneToday: dailyTarget != null && todayDone >= dailyTarget, nextKey: keys[done] ?? null };
}

/** Today's target. On pace (or ahead), what's left is spread over the days left. Behind, whatever you owe from
    earlier days is added to today's own share — a 22-āyah day that starts 9 behind is 31. `doneAtStart` is where
    you were when today began, so the target holds still as you read. */
export function dailyPace(total: number, doneAtStart: number, expected: number, daysLeft: number, cycleLen: number) {
  const remaining = Math.max(0, total - doneAtStart);
  const spread = Math.ceil(remaining / daysLeft);
  const catchUp = Math.max(0, expected - doneAtStart);
  const target = catchUp > 0 ? Math.min(remaining, Math.max(spread, Math.ceil(total / cycleLen) + catchUp)) : spread;
  return { target: Math.max(1, target), catchUp };
}

export const cycleEnd = (k: Khatmah) => {
  const len = cycleDays(k.cycle);
  return len ? addDays(k.cycleStart, len - 1) : null;
};

/* ── Ḥasanāt ───────────────────────────────────────────────────────────
   "Whoever reads a letter from the Book of Allah receives a good deed, and
   the good deed is multiplied by ten" (at-Tirmidhī 2910). We count the
   written Arabic letters of the Uthmani text, ignoring vowel marks, Qurʾanic
   annotation signs, tatweel and āyah numbers. An estimate only — Allah
   multiplies for whom He wills. */
const MARKS = /[ؐ-ًؚ-ٰٟۖ-ۭـ࣓-ࣿ]/g;
const LETTER = /[ء-غف-يٱ-ۓ]/g;

export function letterCount(text: string): number {
  return (text.replace(MARKS, '').match(LETTER) ?? []).length;
}


/** Sum of logged days (missing days count as zero). */
export const sumDays = (days: (AyahDay | undefined)[]) =>
  days.reduce((a, d) => ({ ayahs: a.ayahs + (d?.ayahs ?? 0), hasanat: a.hasanat + (d?.hasanat ?? 0), pages: a.pages + (d?.pages ?? 0) }), { ayahs: 0, hasanat: 0, pages: 0 });

export const hasanatFor = (text: string) => letterCount(text) * 10;

export const PRESETS: { title: string; scope: KhatmahScope; cycle: KhatmahCycle | null }[] = [
  { title: 'Khatm — cover to cover', scope: { kind: 'quran' }, cycle: null },
  { title: 'Khatm in a month', scope: { kind: 'quran' }, cycle: { every: 1, unit: 'month' } },
  { title: 'Yā-Sīn daily', scope: { kind: 'surah', surah: 36 }, cycle: { every: 1, unit: 'day' } },
  { title: 'Al-Mulk nightly', scope: { kind: 'surah', surah: 67 }, cycle: { every: 1, unit: 'day' } },
  { title: 'Al-Kahf every Friday', scope: { kind: 'surah', surah: 18 }, cycle: { every: 1, unit: 'week' } },
  { title: 'Al-Baqarah every 2 weeks', scope: { kind: 'surah', surah: 2 }, cycle: { every: 2, unit: 'week' } },
  { title: 'Al-Wāqiʿah daily', scope: { kind: 'surah', surah: 56 }, cycle: { every: 1, unit: 'day' } },
];
